import { memo, useEffect, useMemo, useState } from 'react';
import {
	calculateProportionalSize,
	COLLECTIONS,
	findNearestLargerSize,
	getCollection,
	getImageById,
} from '~/assets/collections';
import { Spinner } from '~/components/ui/Spinner';
import { rage } from '~/rage';
import { StyledImage, StyledSpinnerContainer } from './ClothesImage.styled';

interface ClothesImageProps extends React.HTMLAttributes<HTMLDivElement> {
	id: string;
	size?: keyof (typeof COLLECTIONS)['clothes']['sizes'] | number;
}

const ClothesImage = ({ id, size = 'md', ...props }: ClothesImageProps) => {
	const [image, setImage] = useState<string | null>(null);

	const collectionData = useMemo(() => getCollection('clothes'), []);

	const nearestSize = useMemo(
		() => typeof size === 'number' && findNearestLargerSize('clothes', size),
		[size],
	);

	const selectedSize = useMemo(() => {
		if (nearestSize) {
			return collectionData.sizes[
				nearestSize as keyof typeof collectionData.sizes
			];
		}
		return collectionData.sizes[size as keyof typeof collectionData.sizes];
	}, [collectionData, nearestSize, size]);

	const displaySize = useMemo(
		() =>
			nearestSize && typeof size === 'number'
				? calculateProportionalSize<'clothes'>(
						'clothes',
						nearestSize as keyof typeof collectionData.sizes,
						size,
				  )
				: selectedSize,
		[collectionData, nearestSize, size, selectedSize],
	);

	const imageSize = useMemo(
		() => nearestSize || (size as keyof typeof collectionData.sizes),
		[nearestSize, size],
	);

	useEffect(() => {
		let cancelled = false;
		setImage(null);

		const fetchImage = async () => {
			try {
				const loadedImage = await getImageById('clothes', id, imageSize);
				if (!cancelled) {
					setImage(loadedImage);
				}
			} catch (error) {
				rage.notifications.showException(error);
			}
		};

		fetchImage().catch((err) => rage.notifications.showException(err));

		return () => {
			cancelled = true;
		};
	}, [id, imageSize]);

	if (!image) {
		return (
			<div {...props} className="clothes-spinner">
				<StyledSpinnerContainer
					style={{
						width: displaySize.w,
						height: displaySize.h,
						margin: '0 auto',
					}}
				>
					<Spinner />
				</StyledSpinnerContainer>
			</div>
		);
	}

	return (
		<StyledImage
			width={displaySize.w}
			height={displaySize.h}
			src={image || ''}
			{...props}
		/>
	);
};

const MemoizedClothesImage = memo(ClothesImage) as typeof ClothesImage;

export { MemoizedClothesImage as ClothesImage };
